import i18next from 'i18next';
import { notificationController } from 'infrastructure/antd/controllers/notification-controller';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from 'store';
import BaseButton from 'components/atoms/baseButton';

import DueDateCell from '../due-date-cell';
import NoteCell from '../note-cell';
import NotePreview from '../note-preview';
import { buildPatientInfo } from '../utils/patient-info';
import { DragHandle } from './table-row';

import type { IBaseTableColumns } from 'components/atoms/base-table/types';
import type {
  IKeyNoteData,
  IQueueTableData,
  TTableName,
} from 'infrastructure/interfaces';

const tableNameMap: Record<TTableName, string> = {
  'follow-up': i18next.t('labels.followUp'),
  newly: i18next.t('labels.newly'),
  partials: i18next.t('labels.partials'),
};

export const useColumns = (): IBaseTableColumns<IQueueTableData> => {
  const {
    ccmConsole: { removeFromQueue, setTableNote, toggleOpenDrawer },
  } = useAppDispatch();
  const { t } = useTranslation();

  return [
    {
      label: t('labels.patientInfo'),
      key: 'name',
      width: '40%',
      align: 'left',
      render(_name: string, record) {
        return <DragHandle record={record} />;
      },
      dataCy: 'queue-table-date',
    },
    {
      label: t('labels.dueDate'),
      key: 'dueDate',
      align: 'left',
      render(dueDate: string | null, record) {
        return <DueDateCell date={dueDate} patientGuid={record.guid} />;
      },
    },
    {
      label: 'Table',
      key: 'tableName',
      align: 'left',
      render(tableName: TTableName) {
        return tableNameMap[tableName];
      },
    },
    {
      label: i18next.t('labels.notes'),
      key: 'notes',
      width: '10%',
      align: 'left',
      render(notes: IKeyNoteData[] = []) {
        return <NotePreview notes={notes} />;
      },
    },
    {
      label: '',
      width: '3%',
      align: 'left',
      key: 'noteaction',
      render(_unused: IKeyNoteData[] = [], record) {
        const noteList = record.notes ?? [];
        return (
          <NoteCell
            length={noteList.length}
            onClick={() => {
              setTableNote({
                notes: noteList,
                tableName: 'partials',
                patientGuid: record.guid,
                patientName: record.name,
                patientInfo: buildPatientInfo(record),
              });
              toggleOpenDrawer();
            }}
          />
        );
      },
      dataCy: 'partials-patients-notes',
    },
    {
      label: t('labels.completed'),
      key: 'guid',
      width: '5%',
      align: 'left',
      render(guid: string, record) {
        return (
          <BaseButton
            type="primary"
            label={t('labels.completed')}
            dataCy="queue-completed-button"
            onClick={() => {
              removeFromQueue({ guid, tableName: record.tableName });
              notificationController.success({
                message: t('messages.patientRemovedFromQueue'),
              });
            }}
          />
        );
      },
    },
  ];
};
